import type { FC } from 'react'

import { Icon, Tooltip } from '@skytech/ui'
import GlobeIcon from 'mdi-react/GlobeIcon'
import InfoVariantCircleOutlineIcon from 'mdi-react/InfoVariantCircleOutlineIcon'
import PinIcon from 'mdi-react/PinIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'

import type { PolicyOrderExtended } from '../types'

const EcomPolicyOrderUser: FC<{ order: PolicyOrderExtended }> = ({ order }) => {
  const translate = useTranslation()

  const {
    name,
    customerName,
    departmentName,
    departmentCode,
    phoneCountryId,
    phoneNo,
    userName,
    deliveryAddress,
    deliveryCity,
    deliveryZip,
    deliveryAddressIsPrivate,
    policyName,
    contactName,
    contactPhoneNumber,
    isGlobalPolicy,
  } = order
  const departmentInfo = departmentName ? `${departmentCode} - ${departmentName}` : '-'
  const userPhoneNumber = phoneNo ? `+${phoneCountryId} ${phoneNo}` : '-'

  return (
    <section>
      <h3>
        <Translation id="User" />
      </h3>

      <DataPoints>
        <DataPoint header={<Translation id="Company" />} value={customerName} />
        <DataPoint header={<Translation id="Department" />} value={departmentInfo} />
        <DataPoint header={<Translation id="Ordered by" />} value={userName || name} />
        <DataPoint
          header={<Translation id="Policy" />}
          value={
            <div className="flex items-center">
              {policyName ? (
                <>
                  <span>{policyName}</span>
                  {isGlobalPolicy ? (
                    <GlobeIcon className="ml-1 h-4 w-4 fill-gray-500" />
                  ) : (
                    <PinIcon className="ml-1 h-4 w-4 fill-gray-500" />
                  )}
                </>
              ) : (
                '—'
              )}
            </div>
          }
        />
        <DataPoint header={<Translation id="User" />} value={name || '—'} />
        <DataPoint header={<Translation id="Phone number" />} value={userPhoneNumber} />
        <DataPoint
          header={
            <span className="relative mb-1.5 flex flex-row items-center gap-2">
              <Translation id="Delivery address" />

              <Tooltip id="delivery-address-info" size="large" />

              <Icon
                isWhiteLabeled={false}
                size="small"
                className="flex items-center"
                data-tooltip-id="delivery-address-info"
                data-tooltip-content={
                  deliveryAddressIsPrivate
                    ? translate('This is a private address')
                    : translate('This is a business address')
                }
              >
                <InfoVariantCircleOutlineIcon />
              </Icon>
            </span>
          }
          value={
            <address>
              {deliveryAddress}
              <br />
              {deliveryZip} {deliveryCity}
            </address>
          }
        />
        {contactName && (
          <DataPoint
            header={<Translation id="Contact info" />}
            value={
              <>
                {contactName}
                <br />
                {contactPhoneNumber}
              </>
            }
          />
        )}
      </DataPoints>
    </section>
  )
}

export default EcomPolicyOrderUser
